import React from "react";
import { MapPin, MessageCircle, Phone } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import PatientAppLayout from "../../../../../components/PatientAppLayout/PatientAppLayout";
import { appointmentMapsHref, appointmentTelHref } from "../../../../../utils/appointmentVenueLinks";
import { displayProviderName } from "../../../../../utils/patientAppointmentsNormalize";
import "./AppointmentConfirmed.scss";

const formatDate = (value) => {
  if (!value) return "Date pending";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "Date pending";
  return d.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const statusText = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "scheduled") return "Scheduled";
  if (key === "completed") return "Completed";
  if (key === "cancelled") return "Cancelled";
  return "Pending admin approval";
};

const statusTone = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "completed") return "done";
  if (key === "scheduled") return "scheduled";
  if (key === "cancelled") return "cancelled";
  return "pending";
};

const fmtINR = (n) => `Rs ${Number(n || 0).toLocaleString("en-IN")}`;

function hasVisitOutcome(appointment) {
  const t = appointment?.visitOutcomeAt;
  return t != null && String(t).trim() !== "";
}

export default function AppointmentConfirmed() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const appointment = state?.appointment || null;
  const mapsUrl = appointment ? appointmentMapsHref(appointment) : null;
  const telUrl = appointment ? appointmentTelHref(appointment.clinicPhone) : null;

  return (
    <PatientAppLayout activeNav="dashboard" backTo="/appointment" backLabel="Appointments">
      <div className="apcPage apcPage--patientShell apcPage--inShellLayout">
        <div className="apcCardsWrap">
          <header className="apcShellHead">
            <h1 className="apcShellHead__title">Appointment details</h1>
            <p className="apcShellHead__lead">Booking and approval status</p>
          </header>
          {!appointment ? (
            <div className="apcDetailCard apcDetailCard--empty">
              <h2 className="apcDetailEmpty__title">No appointment selected</h2>
              <p className="apcDetailEmpty__text">Choose an appointment from your list to see date, location, and status.</p>
              <button
                type="button"
                className="apcDetailBtn apcDetailBtn--primary"
                onClick={() => navigate("/appointment")}
              >
                Go to appointments
              </button>
            </div>
          ) : (
            <div className="apcDetailCard">
              <header className="apcDetailHeader">
                <h2 className="apcDetailHeader__name">
                  {displayProviderName(appointment.providerRole, appointment.providerName)}
                </h2>
                <span className={`apcStatusPill apcStatusPill--${statusTone(appointment.status)}`}>
                  {statusText(appointment.status)}
                </span>
              </header>

              <div className="apcDetailStack">
                <section className="apcDetailBlock" aria-labelledby="apc-when-label">
                  <h3 id="apc-when-label" className="apcDetailBlock__label">
                    When
                  </h3>
                  <p className="apcDetailBlock__value">
                    {formatDate(appointment.appointmentDate)}
                    {appointment.timeSlot ? (
                      <>
                        <span className="apcDetailBlock__dot" aria-hidden>
                          {" "}
                          ·{" "}
                        </span>
                        {appointment.timeSlot}
                      </>
                    ) : null}
                  </p>
                </section>

                <section className="apcDetailBlock" aria-labelledby="apc-where-label">
                  <h3 id="apc-where-label" className="apcDetailBlock__label">
                    Where
                  </h3>
                  {appointment.clinicName ? <p className="apcVenueTitle">{appointment.clinicName}</p> : null}
                  {appointment.clinicAddress ? (
                    <p className="apcAddress">{appointment.clinicAddress}</p>
                  ) : !appointment.clinicName ? (
                    <p className="apcDetailBlock__muted">Clinic location will appear once confirmed.</p>
                  ) : null}
                </section>

                {mapsUrl || telUrl ? (
                  <div className="apcQuickActions">
                    {mapsUrl ? (
                      <a
                        className="apcQuickAction"
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open location in maps"
                      >
                        <MapPin size={18} strokeWidth={2.25} aria-hidden />
                        <span>Maps</span>
                      </a>
                    ) : null}
                    {telUrl ? (
                      <a className="apcQuickAction" href={telUrl} aria-label="Call clinic">
                        <Phone size={18} strokeWidth={2.25} aria-hidden />
                        <span>Call</span>
                      </a>
                    ) : null}
                  </div>
                ) : null}

                {appointment.noteForDoctor ? (
                  <section className="apcDetailBlock apcDetailBlock--note" aria-label="Your note">
                    <h3 className="apcDetailBlock__label">Your note</h3>
                    <p className="apcDetailNote">{appointment.noteForDoctor}</p>
                  </section>
                ) : null}

                {hasVisitOutcome(appointment) ? (
                  <div className="apcVisitBlock">
                    <p className="apcVisitBlock__title">After your visit</p>
                    {appointment.visitPatientAttended === false ? (
                      <p className="apcDoctorExp">Your provider recorded that you did not attend this scheduled visit.</p>
                    ) : null}
                    {appointment.visitPatientAttended === true ? (
                      <>
                        <p className="apcDoctorExp">Your provider confirmed you attended.</p>
                        {appointment.visitProcedureProceeding === true ? (
                          <>
                            <p className="apcDoctorExp">Procedure proceeding as planned.</p>
                            {appointment.visitSamePriceAsBooked === true && appointment.visitBookedPriceInr != null ? (
                              <p className="apcDoctorExp">Agreed price unchanged: {fmtINR(appointment.visitBookedPriceInr)}.</p>
                            ) : null}
                            {appointment.visitSamePriceAsBooked === false ? (
                              <>
                                <p className="apcDoctorExp">
                                  Updated agreed amount: {fmtINR(appointment.visitFinalAgreedPriceInr)}.
                                </p>
                                {appointment.visitPriceChangeReason ? (
                                  <p className="apcDoctorExp">Reason: {appointment.visitPriceChangeReason}</p>
                                ) : null}
                              </>
                            ) : null}
                          </>
                        ) : null}
                        {appointment.visitProcedureProceeding === false ? (
                          <p className="apcDoctorExp">
                            Your provider indicated the procedure is not proceeding as originally planned for this visit.
                          </p>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <footer className="apcDetailFooter">
                <p className="apcDetailFooter__hint">Questions about this booking? Continue in your bid chat.</p>
                <button
                  type="button"
                  className="apcDetailBtn apcDetailBtn--primary"
                  onClick={() => navigate("/patient-chats")}
                >
                  <MessageCircle size={20} strokeWidth={2.25} aria-hidden />
                  Open messages
                </button>
              </footer>
            </div>
          )}
        </div>
      </div>
    </PatientAppLayout>
  );
}
